import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { daDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { MessageSquarePlus, RotateCcw, History, ChevronDown, ChevronRight, Flag, Paperclip, FileText, Download } from "lucide-react";
import { OriginFields } from "@/components/le/OriginPicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ACTION_STATUSES = ["Åben", "I gang", "Lukket"];
export const LE_GUESTS = ["Mikael K", "Brian"];
const withGuests = (users) => [...(users || []), ...LE_GUESTS.map((g) => ({ id: `guest:${g}`, name: g }))];
export const ST_BADGE = {
  Åben: "bg-[#F5F4F2] text-stone-600 border-[#EBE8E3]",
  "I gang": "bg-[#EFE7DC] text-[#8A6D3B] border-[#E3D5C0]",
  Lukket: "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]",
};

function Field({ label, children }) {
  return <div><p className="text-xs font-medium text-stone-500 mb-1.5">{label}</p>{children}</div>;
}

// ---- Bilag/dokumentation (genbruges på handling, mål og delingsside) ----
export function AttachmentList({ files, hrefFor, label = "Dokumentation" }) {
  if (!(files || []).length) return null;
  return (
    <div className="mt-2.5" data-testid="attachment-list">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 flex items-center gap-1"><Paperclip className="h-3 w-3" /> {label}</p>
      <div className="mt-1.5 space-y-1">
        {files.map((fl) => (
          <a key={fl.id} href={hrefFor(fl)} target="_blank" rel="noreferrer" data-testid={`attachment-${fl.id}`}
            className="flex items-center gap-2 text-sm text-[#1A1919] hover:border-stone-300 rounded-lg border border-[#EBE8E3] bg-white px-2.5 py-1.5 transition-colors">
            <FileText className="h-3.5 w-3.5 text-stone-400 shrink-0" />
            <span className="truncate flex-1">{fl.original_filename}</span>
            {fl.size ? <span className="text-xs text-stone-400 shrink-0">{Math.max(1, Math.round((fl.size || 0) / 1024))} KB</span> : null}
            <Download className="h-3.5 w-3.5 text-stone-400 shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

// ---- Ny/redigér handling ----
export function ActionFormDialog({ open, onOpenChange, users, action, meeting, agendaItem, goal, source, input, onSaved }) {
  const editing = !!action;
  const [desc, setDesc] = useState(action?.description || "");
  const [respId, setRespId] = useState(action?.responsible_id || null);
  const [coIds, setCoIds] = useState(action?.co_responsible_ids || []);
  const [deadline, setDeadline] = useState(action?.deadline || null);
  const [status, setStatus] = useState(action?.status || "Åben");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(meeting ? "meeting" : "between");
  const [meetingId, setMeetingId] = useState(meeting?.id || null);
  const [agendaKey, setAgendaKey] = useState(agendaItem?.key || null);
  const [sources, setSources] = useState([
    ...(goal ? [{ type: "goal", id: goal.id, no: goal.goal_no, title: goal.title, locked: true }] : []),
    ...(source ? [{ ...source, locked: true }] : []),
  ]);
  const uopts = meeting
    ? (() => { const pid = new Set(meeting.participant_ids || []); const act = (users || []).filter((u) => u.active !== false);
        return [...act.filter((u) => pid.has(u.id)), ...(meeting.guests || []).map((g) => ({ id: `guest:${g}`, name: g })), ...act.filter((u) => !pid.has(u.id))]; })()
    : withGuests(users);

  const save = async () => {
    if (!desc.trim()) { toast.error("Angiv en beskrivelse"); return; }
    if (mode === "meeting" && !meetingId) { toast.error("Vælg LE-møde — eller «Godkendt mellem møder»"); return; }
    setSaving(true);
    try {
      if (editing) {
        const r = await api.patch(`/le/actions/${action.id}`, {
          description: desc, responsible_id: respId, co_responsible_ids: coIds, deadline });
        onSaved?.(r.data);
      } else {
        const li = input ? [{ input_id: input.id, type: input.type, label: input.type, text: input.text || input.caption || "" }] : [];
        const r = await api.post("/le/actions", {
          description: desc, responsible_id: respId, co_responsible_ids: coIds, deadline, status,
          origin_meeting_id: mode === "meeting" ? meetingId : null,
          origin_agenda_key: mode === "meeting" ? agendaKey : null,
          origin_agenda_title: mode === "meeting" && agendaItem?.key === agendaKey ? agendaItem?.title : null,
          sources: sources.map((s) => ({ type: s.type, id: s.id })), linked_inputs: li });
        onSaved?.(r.data);
      }
      toast.success(editing ? "Handling opdateret" : "Handling oprettet");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="le-action-form">
        <DialogHeader><DialogTitle>{editing ? "Redigér handling" : "Ny handling"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Beskrivelse">
            <Textarea data-testid="le-action-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="rounded-xl" placeholder="Hvad skal der gøres?" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ansvarlig"><UserSingleSelect users={uopts} value={respId} onChange={setRespId} testid="le-action-resp" className="h-10" placeholder="Vælg ansvarlig" /></Field>
            <Field label="Frist"><DanishDatePicker value={deadline} onChange={setDeadline} testid="le-action-deadline" allowClosed /></Field>
          </div>
          <Field label="Medansvarlige"><ResponsibleMultiSelect users={uopts} value={coIds} onChange={setCoIds} testid="le-action-co" className="h-10" /></Field>
          {!editing && (
            <Field label="Status">
              <div className="inline-flex rounded-xl border border-[#EBE8E3] p-1">
                {ACTION_STATUSES.map((s) => (
                  <button key={s} type="button" data-testid={`le-action-status-${s}`} onClick={() => setStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${status === s ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{s}</button>
                ))}
              </div>
            </Field>
          )}
          {!editing && (
            <OriginFields mode={mode} setMode={setMode} meetingId={meetingId} agendaKey={agendaKey} lockMeeting={!!meeting}
              setPoint={({ meetingId: m, agendaKey: k }) => { setMeetingId(m); setAgendaKey(k); }} sources={sources} setSources={setSources} testid="le-action-origin" />
          )}
          {input && !editing && <p className="text-xs text-stone-500">Knyttes til input: {input.type === "note" ? `note «${(input.text || "").slice(0, 40)}»` : input.type}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Annullér</Button>
          <Button data-testid="le-action-save" onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving ? "Gemmer…" : "Gem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Tilføj opfølgning ----
export function FollowupDialog({ open, onOpenChange, users, action, meeting, onSaved }) {
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState(action?.status || "Åben");
  const [deadline, setDeadline] = useState(action?.deadline || null);
  const [respId, setRespId] = useState(action?.responsible_id || null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const changed = status !== action.status || deadline !== action.deadline || (respId || null) !== (action.responsible_id || null);
    if (!comment.trim() && !changed) { toast.error("Skriv en kommentar eller foretag en ændring"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/le/actions/${action.id}/followup`, {
        comment, new_status: status, new_deadline: deadline, new_responsible_id: respId,
        meeting_id: meeting?.id || null });
      onSaved?.(r.data);
      toast.success("Opfølgning tilføjet");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="le-followup-form">
        <DialogHeader><DialogTitle>Tilføj opfølgning · {action?.action_no}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Kommentar / statusnote">
            <Textarea data-testid="le-followup-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="rounded-xl" placeholder="Hvad blev gjort · hvad blev målt · resultat" />
            <p className="text-[11px] text-stone-500 mt-1">Hvad blev gjort · hvad blev målt · resultat</p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <div className="inline-flex rounded-xl border border-[#EBE8E3] p-1 w-full">
                {ACTION_STATUSES.map((s) => (
                  <button key={s} type="button" data-testid={`le-followup-status-${s}`} onClick={() => setStatus(s)}
                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs ${status === s ? "bg-[#A83232] text-white" : "text-stone-600"}`}>{s}</button>
                ))}
              </div>
            </Field>
            <Field label="Frist"><DanishDatePicker value={deadline} onChange={setDeadline} testid="le-followup-deadline" allowClosed /></Field>
          </div>
          <Field label="Ansvarlig"><UserSingleSelect users={withGuests(users)} value={respId} onChange={setRespId} testid="le-followup-resp" className="h-10" placeholder="Vælg ansvarlig" /></Field>
          {status === "Lukket" && <p className="text-xs text-[#5f6b4a]">Handlingen lukkes. Kommentaren gemmes som afsluttende bemærkning sammen med dit navn og dato.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Annullér</Button>
          <Button data-testid="le-followup-save" onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving ? "Gemmer…" : "Gem opfølgning"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowupRow({ f }) {
  const chips = [];
  if (f.status_to) chips.push(`Status: ${f.status_from || "—"} → ${f.status_to}`);
  if (f.deadline_to !== undefined && ("deadline_to" in f)) chips.push(`Frist: ${daDate(f.deadline_from)} → ${daDate(f.deadline_to)}`);
  if (f.responsible_to !== undefined && ("responsible_to" in f)) chips.push(`Ansvarlig: ${f.responsible_from || "—"} → ${f.responsible_to || "—"}`);
  return (
    <div className="border-l-2 border-[#EBE8E3] pl-3 py-1.5">
      <div className="flex items-center gap-2 text-[11px] text-stone-400">
        <span>{daDate(f.at)}</span><span>·</span><span>{f.by_name || "—"}</span>
        {f.meeting_title && <><span>·</span><span className="text-[#806B61]">{f.meeting_title}</span></>}
      </div>
      {f.comment && <p className="text-sm text-stone-700 mt-0.5 whitespace-pre-wrap">{f.comment}</p>}
      {chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1">{chips.map((c, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">{c}</span>)}</div>}
    </div>
  );
}

// ---- Genåbn handling ----
export function ReopenDialog({ open, onOpenChange, action, onSaved }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const r = await api.post(`/le/actions/${action.id}/reopen`, { comment }); onSaved?.(r.data); toast.success("Handling genåbnet"); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="le-reopen-form">
        <DialogHeader><DialogTitle>Genåbn handling · {action?.action_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-stone-500">Den tidligere historik bevares. Handlingen sættes til «I gang».</p>
          <Field label="Bemærkning (valgfri)"><Textarea data-testid="le-reopen-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="rounded-xl" placeholder="Hvorfor genåbnes handlingen?" /></Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Annullér</Button>
          <Button data-testid="le-reopen-save" onClick={save} disabled={saving} className="rounded-xl bg-[#A83232] hover:bg-[#8f2a2a]">{saving ? "Genåbner…" : "Genåbn"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Handlingskort ----
export function ActionCard({ action, users, meeting, onChanged, compact = false }) {
  const [open, setOpen] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const a = action;
  const latest = a.latest_followup;

  return (
    <div className="bg-white rounded-2xl border border-[#EBE8E3] p-4" data-testid={`le-action-card-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-stone-400">{a.action_no}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ST_BADGE[a.status]}`} data-testid={`le-action-status-badge-${a.id}`}>{a.status}</span>
            {a.overdue && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#A83232] text-white flex items-center gap-1" data-testid={`le-action-overdue-${a.id}`}><Flag className="h-3 w-3" /> Overskredet</span>}
          </div>
          <p className="text-sm font-medium text-[#1A1919] mt-1.5 whitespace-pre-wrap">{a.description}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-stone-500">
            <span>Ansvarlig: <span className="text-stone-700">{a.responsible_name || "—"}</span></span>
            {(a.co_responsible_names || []).length > 0 && <span>Med: {a.co_responsible_names.join(", ")}</span>}
            <span className={a.overdue ? "text-[#A83232] font-medium" : ""}>Frist: {daDate(a.deadline)}</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-stone-500" data-testid={`le-action-origin-${a.id}`}>
            <span>Oprettet af <span className="text-stone-700">{a.created_by_name || "—"}</span>{a.created_at ? ` · ${daDate(a.created_at)}` : ""}</span>
            {a.origin_meeting_title && (
              <span>Fra mødet <span className="text-stone-700">{a.origin_meeting_title}</span>{a.origin_meeting_date ? ` (${daDate(a.origin_meeting_date)})` : ""}{a.origin_agenda_title && a.origin_agenda_title !== a.origin_meeting_title ? <> · punkt: <span className="text-stone-700">{a.origin_agenda_title}</span></> : null}
              </span>
            )}
            {!a.origin_meeting_title && a.origin_agenda_title && <span>Punkt: <span className="text-stone-700">{a.origin_agenda_title}</span></span>}
            {a.origin_goal_title && (
              <span>Tilknyttet mål <span className="text-stone-700">{a.origin_goal_title}</span>
              </span>
            )}
            <Link to={`/handling/${a.id}`} data-testid={`le-action-open-${a.id}`} className="text-[#A83232] font-medium hover:underline">Åbn handling</Link>
          </div>
          {latest && (
            <div className="mt-2 text-xs bg-[#FBFAF8] rounded-lg px-3 py-2 border border-[#F0EDE8]">
              <span className="text-stone-400">Seneste opfølgning ({daDate(latest.at)}, {latest.by_name}): </span>
              <span className="text-stone-700">{latest.comment || (latest.status_to ? `Status → ${latest.status_to}` : "—")}</span>
            </div>
          )}
          {(a.files || []).length > 0 && <AttachmentList files={a.files} hrefFor={(fl) => `${BACKEND}/api/le/actions/${a.id}/files/${fl.id}`} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Button size="sm" data-testid={`le-action-followup-btn-${a.id}`} onClick={() => setFuOpen(true)} className="rounded-lg h-8 bg-[#A83232] hover:bg-[#8f2a2a]"><MessageSquarePlus className="h-3.5 w-3.5 mr-1" /> Tilføj opfølgning</Button>
        {a.status === "Lukket" && <Button size="sm" variant="outline" data-testid={`le-action-reopen-${a.id}`} onClick={() => setReopenOpen(true)} className="rounded-lg h-8 border-[#EBE8E3]"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Genåbn</Button>}
        {!compact && <button data-testid={`le-action-edit-${a.id}`} onClick={() => setEditOpen(true)} className="text-xs text-stone-500 hover:text-stone-800 hover:underline ml-1">Redigér</button>}
        {(a.followup_count > 0) && (
          <button data-testid={`le-action-history-${a.id}`} onClick={() => setOpen((o) => !o)} className="text-xs text-[#806B61] hover:underline flex items-center gap-1 ml-auto">
            <History className="h-3.5 w-3.5" /> {a.followup_count} opfølgning(er) {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-1" data-testid={`le-action-history-list-${a.id}`}>
          {(a.followups || []).slice().reverse().map((f) => <FollowupRow key={f.id} f={f} />)}
        </div>
      )}
      {fuOpen && <FollowupDialog open={fuOpen} onOpenChange={setFuOpen} users={users} action={a} meeting={meeting} onSaved={onChanged} />}
      {editOpen && <ActionFormDialog open={editOpen} onOpenChange={setEditOpen} users={users} action={a} onSaved={onChanged} />}
      {reopenOpen && <ReopenDialog open={reopenOpen} onOpenChange={setReopenOpen} action={a} onSaved={onChanged} />}
    </div>
  );
}
